import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import AppSelect from '../../components/AppSelect.jsx'

const POV_OPTIONS = [
  { value: 'first',         label: 'First person (I, me)' },
  { value: 'third-close',   label: 'Third person close (she/he, interior access)' },
  { value: 'third-distant', label: 'Third person distant (observational)' },
  { value: 'second',        label: 'Second person (you)' },
  { value: 'multiple',      label: 'Multiple POVs' },
]
const TENSE_OPTIONS = [
  { value: 'past',    label: 'Past tense (she walked)' },
  { value: 'present', label: 'Present tense (she walks)' },
]
const DENSITY_OPTIONS = [
  { value: 'spare',   label: 'Spare — short sentences, white space, restraint' },
  { value: 'rich',    label: 'Rich — layered, detailed, sensory' },
  { value: 'varied',  label: 'Varied — alternates to suit the moment' },
]
const DISTANCE_OPTIONS = [
  { value: 'deep', label: 'Deep interiority — we feel everything they feel' },
  { value: 'mid',  label: 'Mid-distance — close but not overwhelming' },
  { value: 'far',  label: 'Far — camera-like, external observation' },
]

const STYLE_TIPS = {
  pov: {
    first: '<strong>First person</strong> creates deep intimacy and a strong sense of voice, but limits you to what your narrator can know. Be careful of "I" fatigue — vary your sentence openings. The narrator\'s unreliability can be a powerful tool.',
    'third-close': '<strong>Third person close</strong> is the most flexible choice — you get interiority without the "I" constraint, and you can occasionally pull back for wider perspective. Most contemporary literary fiction uses this.',
    'third-distant': '<strong>Third person distant</strong> creates an observational, almost cinematic quality. It suits stories where atmosphere and event matter more than interiority.',
    second: '<strong>Second person</strong> is rare and intense — it pulls readers directly into the story but can feel gimmicky if not handled with care. Works best in short, atmospheric pieces.',
    multiple: '<strong>Multiple POVs</strong> allow you to show the same events from different angles, which is great for complex plots — but each voice must be genuinely distinct, or readers lose the thread.',
  },
  tense: {
    past: '<strong>Past tense</strong> is the default of fiction. It has a storytelling quality — "this is what happened" — and gives you freedom to use the full range of past tenses for rhythm and timing.',
    present: '<strong>Present tense</strong> creates urgency and immediacy. It works especially well in thrillers, literary fiction, and stories with a dreamlike quality. Be aware that it can feel breathless if overused.',
  },
}

export default function StyleVoice({ pid = 'w' }) {
  const [project, setProject] = useState({})
  const [styleTip, setStyleTip] = useState(null)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('writingVoiceCollapsed') === 'true'
  )

  useEffect(() => { api.getWritingProjectData(pid).then(d => setProject(d || {})) }, [pid])

  function set(field, value) {
    setProject(prev => {
      const next = { ...prev, [field]: value }
      api.saveWritingProjectData(pid, next)
      return next
    })
  }

  function onStyleChange(type, value) {
    set(type === 'pov' ? 'pov' : 'tense', value)
    const tip = STYLE_TIPS[type]?.[value]
    setStyleTip(tip ? { html: tip } : null)
  }

  function toggleCollapsed() {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem('writingVoiceCollapsed', String(next))
  }

  return (
    <Card label="Section 9" title="Style & voice">
      <button className="section-collapse-btn" onClick={toggleCollapsed}>
        {collapsed ? '▼ Show section' : '▲ Collapse section'}
      </button>

      {!collapsed && (
        <>
          <TipBox title="💡 Finding your prose voice" collapsible>
            <p>Voice is the most personal element of writing — it's the personality behind the words. It emerges from your choices: sentence length, what details you notice, what you leave unsaid, how close you sit to your character's interiority. The best thing you can do is read widely and write without self-censoring in early drafts.</p>
          </TipBox>

          <p style={{ fontSize: '0.82rem', color: 'var(--bark)', marginBottom: '0.75rem', fontWeight: 500, marginTop: '1.25rem' }}>
            Set your stylistic intentions:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: '1rem' }}>
            <div>
              <label className="w-label">Point of view</label>
              <AppSelect
                value={project.pov || ''}
                onChange={v => onStyleChange('pov', v)}
                options={POV_OPTIONS}
                placeholder="Choose..."
              />
            </div>
            <div>
              <label className="w-label">Tense</label>
              <AppSelect
                value={project.tense || ''}
                onChange={v => onStyleChange('tense', v)}
                options={TENSE_OPTIONS}
                placeholder="Choose..."
              />
            </div>
            <div>
              <label className="w-label">Prose density</label>
              <AppSelect
                value={project.density || ''}
                onChange={v => set('density', v)}
                options={DENSITY_OPTIONS}
                placeholder="Choose..."
              />
            </div>
            <div>
              <label className="w-label">Narrative distance</label>
              <AppSelect
                value={project.distance || ''}
                onChange={v => set('distance', v)}
                options={DISTANCE_OPTIONS}
                placeholder="Choose..."
              />
            </div>
          </div>

          {styleTip && (
            <div className="tip-box">
              <p className="tip-box-title">About your choice</p>
              <p dangerouslySetInnerHTML={{ __html: styleTip.html }} />
            </div>
          )}

          <TipBox title="📖 Show don't tell — a quick guide" collapsible>
            <ul>
              <li><strong>Telling:</strong> She was nervous. &nbsp;→&nbsp; <strong>Showing:</strong> She checked her phone three times before putting it face-down.</li>
              <li><strong>Telling:</strong> He was cruel. &nbsp;→&nbsp; <strong>Showing:</strong> He corrected her grammar in front of their friends and smiled when she flushed.</li>
              <li>Use telling to move quickly. Use showing for moments that matter.</li>
            </ul>
          </TipBox>

          <TipBox title="Finding your voice" collapsible>
            <ul>
              <li>Read your work aloud. Every sentence that makes you stumble needs rewriting.</li>
              <li>Voice comes from what you choose to notice and what you choose to leave out — not from unusual words.</li>
              <li>Your natural speaking voice, relaxed and unguarded, is closer to your writing voice than you think.</li>
              <li>Read writers whose voice you admire and copy them deliberately for one page. Then throw it away. Something will remain.</li>
            </ul>
          </TipBox>
        </>
      )}
    </Card>
  )
}
